import { gql, MutationHookOptions, useMutation } from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLAuthentication,
  GQLMutationSecurity_AuthenticateWithEmailAndPasswordArgs,
  GQLMutationSecurity_AuthenticateWithJwtTokenArgs,
  GQLMutationSecurity_ImpersonateArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { SECURITY_CONTEXT_FIELDS } from './fragments/security';

export class SecurityOperation {
  authenticationFromResponse(data: any): GQLAuthentication {
    if (data === undefined) {
      throw new Exception(`Cannot extract authentication from undefined input data`);
    }
    if (data.Security_authenticateWithEmailAndPassword !== undefined) {
      return data.Security_authenticateWithEmailAndPassword;
    }
    if (data.Security_authenticateWithJwtToken !== undefined) {
      return data.Security_authenticateWithJwtToken;
    }
    if (data.Security_impersonate !== undefined) {
      return data.Security_impersonate;
    }
    throw new Exception(`Cannot extract authentication from expected fields in input data`);
  }

  authenticateWithEmailAndPassword(
    options: MutationHookOptions<
      GQLAuthentication,
      GQLMutationSecurity_AuthenticateWithEmailAndPasswordArgs
    > = {}
  ) {
    const mutation = gql`
      ${SECURITY_CONTEXT_FIELDS}
      mutation authenticateWithEmailAndPassword($email: String!, $password: String!) {
        Security_authenticateWithEmailAndPassword(email: $email, password: $password) {
          token
          securityContext {
            ...SecurityContextFields
          }
        }
      }
    `;
    return useMutation<GQLAuthentication, GQLMutationSecurity_AuthenticateWithEmailAndPasswordArgs>(
      mutation,
      options
    );
  }

  authenticateWithJwtToken(
    options: MutationHookOptions<GQLAuthentication, GQLMutationSecurity_AuthenticateWithJwtTokenArgs> = {}
  ) {
    const mutation = gql`
      ${SECURITY_CONTEXT_FIELDS}
      mutation Security_authenticateWithJwtToken($token: String!) {
        Security_authenticateWithJwtToken(token: $token) {
          token
          securityContext {
            ...SecurityContextFields
          }
        }
      }
    `;
    return useMutation<GQLAuthentication, GQLMutationSecurity_AuthenticateWithJwtTokenArgs>(
      mutation,
      options
    );
  }

  impersonate(options: MutationHookOptions<GQLAuthentication, GQLMutationSecurity_ImpersonateArgs> = {}) {
    const mutation = gql`
      ${SECURITY_CONTEXT_FIELDS}
      mutation Security_impersonate($targetPartyId: ID!) {
        Security_impersonate(targetPartyId: $targetPartyId) {
          token
          securityContext {
            ...SecurityContextFields
          }
        }
      }
    `;
    return useMutation<GQLAuthentication, GQLMutationSecurity_ImpersonateArgs>(mutation, options);
  }

  logout(options: MutationHookOptions = {}) {
    const mutation = gql`
      mutation {
        Security_logout
      }
    `;
    return useMutation(mutation, options);
  }
}
